import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Loader2,
  Pencil,
  Pin,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { ViewToggleButton } from "@/components/ui/ViewToggleButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { TagBadge } from "@/components/ui/TagBadge";
import { useViewMode } from "@/hooks/useViewMode";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";

const NOTE_COLUMNS: ColumnDef[] = [
  { key: "title", label: "Title" },
  { key: "type", label: "Type" },
  { key: "status", label: "Status" },
  { key: "context", label: "Context" },
  { key: "author", label: "Author" },
  { key: "date", label: "Date" },
];
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import {
  useDeleteNote,
  useNoteList,
  useNoteStatuses,
  useNoteTags,
  useNoteTypes,
  useTogglePin,
} from "../hooks/useNotes";
import { NewNoteDrawer } from "../components/NewNoteDrawer";
import { NoteCard } from "../components/NoteCard";
import type { NoteListItem } from "../types";

const BORDER = "hsl(210,30%,85%)";

function formatRelative(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} days ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function fullDate(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  general: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
  meeting_minutes: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  technical_doc: { bg: "hsl(270,70%,96%)", text: "hsl(270,60%,42%)" },
  decision_log: { bg: "hsl(180,60%,93%)", text: "hsl(180,70%,28%)" },
  reference: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)" },
  personal: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
  published: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)" },
  archived: { bg: "hsl(40,95%,94%)", text: "hsl(35,85%,38%)" },
};

const iconBtnBase =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-muted hover:text-foreground";
const iconBtnDelete =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]";

export default function NoteListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [projectFilter, setProjectFilter] = useState("");
  const [meetingFilter, setMeetingFilter] = useState("");
  const { mode: viewMode, toggle: toggleViewMode } = useViewMode("notes", "list");
  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("notes", NOTE_COLUMNS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const { data: projectsResult } = useProjectList({ page: 1, limit: 100 });
  const projects = projectsResult?.data ?? [];
  const { data: meetingsResult } = useMeetingList({ page: 1, limit: 100 });
  const meetings = meetingsResult?.data ?? [];

  const noteTypes = useNoteTypes().data ?? [];
  const noteStatuses = useNoteStatuses().data ?? [];
  const noteTagOptions = useNoteTags().data ?? [];

  const tagLabels = useMemo(
    () => Object.fromEntries(noteTagOptions.map((t) => [t.value, t.label])),
    [noteTagOptions],
  );
  const typeLabels = useMemo(
    () => Object.fromEntries(noteTypes.map((t) => [t.value, t.label])),
    [noteTypes],
  );
  const statusLabels = useMemo(
    () => Object.fromEntries(noteStatuses.map((s) => [s.value, s.label])),
    [noteStatuses],
  );

  const { data, isLoading, isError, error } = useNoteList({
    page,
    limit: pageSize,
    search: search || undefined,
    projectId: projectFilter || undefined,
    meetingId: meetingFilter || undefined,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
  });

  const togglePin = useTogglePin();
  const deleteNote = useDeleteNote();

  const allNotes: NoteListItem[] = data?.data ?? [];
  const pinned = useMemo(() => allNotes.filter((n) => n.isPinned), [allNotes]);
  const unpinned = useMemo(
    () => allNotes.filter((n) => !n.isPinned),
    [allNotes],
  );
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  async function handleTogglePin(n: NoteListItem) {
    await togglePin.mutateAsync({ id: n.id, isPinned: !n.isPinned });
    showToast(n.isPinned ? "Note unpinned" : "Note pinned");
  }

  function handleDelete(e: React.MouseEvent, n: NoteListItem) {
    e.stopPropagation();
    setDeleteTarget({ id: n.id, name: n.title });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteNote.mutateAsync(deleteTarget.id);
    showToast("Note deleted");
  }

  function openNote(id: string) {
    navigate(`/notes/${id}`);
  }

  function openEdit(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    navigate(`/notes/${id}?edit=true`);
  }

  function toggleTag(value: string) {
    setPage(1);
    setSelectedTags((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }

  function renderTable(notes: NoteListItem[]) {
    return (
      <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
        <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-[14px] py-[9px]" style={{ width: 110 }}>#</th>
            {isVisible("title") && (
              <th className="px-[14px] py-[9px]">Title & Tags</th>
            )}
            {isVisible("type") && (
              <th className="px-[14px] py-[9px]" style={{ width: 130 }}>Type</th>
            )}
            {isVisible("status") && (
              <th className="px-[14px] py-[9px]" style={{ width: 110 }}>Status</th>
            )}
            {isVisible("context") && (
              <th className="px-[14px] py-[9px]">Context</th>
            )}
            {isVisible("author") && (
              <th className="px-[14px] py-[9px]" style={{ width: 140 }}>Author</th>
            )}
            {isVisible("date") && (
              <th className="px-[14px] py-[9px]" style={{ width: 110 }}>Date</th>
            )}
            <th className="px-[14px] py-[9px] text-right" style={{ width: 140 }}>Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {notes.map((n) => {
            const chain = [n.companyName, n.productName, n.projectName]
              .filter(Boolean)
              .join(" › ");
            const typeColor = TYPE_COLORS[n.type] ?? TYPE_COLORS.general;
            const statusColor = STATUS_COLORS[n.status] ?? STATUS_COLORS.draft;
            return (
              <tr
                key={n.id}
                className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                onClick={() => openNote(n.id)}
              >
                <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                  {n.code ?? "—"}
                </td>
                {isVisible("title") && (
                  <td className="px-[14px] py-[11px]">
                    <div className="flex items-center gap-1.5">
                      {n.isPinned && (
                        <Pin
                          className="h-3 w-3"
                          strokeWidth={1.5}
                          style={{ color: "hsl(40,95%,50%)", fill: "hsl(40,95%,55%)" }}
                        />
                      )}
                      <span className="font-medium text-foreground">{n.title}</span>
                    </div>
                    {n.tags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {n.tags.slice(0, 4).map((t) => (
                          <TagBadge key={t} value={t} label={tagLabels[t] ?? t} size="sm" />
                        ))}
                        {n.tags.length > 4 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{n.tags.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                )}
                {isVisible("type") && (
                  <td className="px-[14px] py-[11px]">
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                      style={{ background: typeColor.bg, color: typeColor.text }}
                    >
                      {typeLabels[n.type] ?? n.type}
                    </span>
                  </td>
                )}
                {isVisible("status") && (
                  <td className="px-[14px] py-[11px]">
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                      style={{ background: statusColor.bg, color: statusColor.text }}
                    >
                      {statusLabels[n.status] ?? n.status}
                    </span>
                  </td>
                )}
                {isVisible("context") && (
                  <td className="px-[14px] py-[11px] text-muted-foreground">
                    <div className="text-[12px]">{chain || "—"}</div>
                  </td>
                )}
                {isVisible("author") && (
                  <td className="px-[14px] py-[11px] text-foreground">
                    {n.createdByName ?? "—"}
                  </td>
                )}
                {isVisible("date") && (
                  <td
                    className="px-[14px] py-[11px] text-muted-foreground"
                    title={fullDate(n.createdAt)}
                  >
                    {formatRelative(n.createdAt)}
                  </td>
                )}
                <td
                  className="px-[14px] py-[11px] text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => openNote(n.id)}
                      className={iconBtnBase}
                      title="View"
                    >
                      <Eye className="h-[15px] w-[15px]" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => openEdit(e, n.id)}
                      className={iconBtnBase}
                      title="Edit"
                    >
                      <Pencil className="h-[15px] w-[15px]" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTogglePin(n)}
                      className={iconBtnBase}
                      title={n.isPinned ? "Unpin" : "Pin"}
                    >
                      <Pin
                        className="h-[15px] w-[15px]"
                        strokeWidth={1.5}
                        style={{
                          color: n.isPinned ? "hsl(40,95%,50%)" : undefined,
                          fill: n.isPinned ? "hsl(40,95%,55%)" : "none",
                        }}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, n)}
                      className={iconBtnDelete}
                      title="Delete"
                    >
                      <Trash2 className="h-[15px] w-[15px]" strokeWidth={1.5} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function renderGrid(notes: NoteListItem[]) {
    return (
      <div
        className="grid gap-3 p-3"
        style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
      >
        {notes.map((n) => (
          <NoteCard
            key={n.id}
            note={n}
            onClick={() => openNote(n.id)}
            onTogglePin={() => handleTogglePin(n)}
            tagLabels={tagLabels}
            typeLabels={typeLabels}
          />
        ))}
      </div>
    );
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Notes" },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Notes</h1>
          </div>
          <div className="flex items-center gap-2">
            <ViewToggleButton mode={viewMode} onToggle={toggleViewMode} />
            <ExportButton
              module="notes"
              filters={{
                search: search || undefined,
                type: typeFilter || undefined,
                status: statusFilter || undefined,
                projectId: projectFilter || undefined,
                meetingId: meetingFilter || undefined,
              }}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={NOTE_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Note
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load notes</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            {pinned.length > 0 && (
              <div className="space-y-2">
                <div
                  className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wide"
                  style={{ color: "hsl(40,95%,35%)" }}
                >
                  <Pin
                    className="h-3 w-3"
                    strokeWidth={1.5}
                    style={{ color: "hsl(40,95%,50%)", fill: "hsl(40,95%,55%)" }}
                  />
                  Pinned
                </div>
                <div
                  className="flex gap-3 overflow-x-auto pb-2"
                  style={{ scrollbarWidth: "thin" }}
                >
                  {pinned.map((n) => (
                    <div key={n.id} style={{ minWidth: 260, maxWidth: 260 }}>
                      <NoteCard
                        note={n}
                        onClick={() => openNote(n.id)}
                        onTogglePin={() => handleTogglePin(n)}
                        tagLabels={tagLabels}
                        typeLabels={typeLabels}
                      />
                    </div>
                  ))}
                </div>
                <div className="border-b" style={{ borderColor: BORDER }} />
              </div>
            )}

            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px] shrink-0">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search notes..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={typeFilter}
                onChange={(e) => resetPage(setTypeFilter)(e.target.value)}
                className="w-44 shrink-0"
              >
                <option value="">All Types</option>
                {noteTypes.map((t) => (
                  <option key={t.id} value={t.value}>{t.label}</option>
                ))}
              </Select>
              <Select
                value={statusFilter}
                onChange={(e) => resetPage(setStatusFilter)(e.target.value)}
                className="w-40 shrink-0"
              >
                <option value="">All Status</option>
                {noteStatuses.map((s) => (
                  <option key={s.id} value={s.value}>{s.label}</option>
                ))}
              </Select>

              {/* Tag multi-select dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setTagPickerOpen((v) => !v)}
                  className="inline-flex h-9 items-center gap-2 rounded-md border bg-white px-3 text-sm"
                  style={{ borderColor: BORDER }}
                >
                  Tags
                  {selectedTags.length > 0 && (
                    <span
                      className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold"
                      style={{ background: "hsl(210,55%,88%)", color: "hsl(210,100%,25%)" }}
                    >
                      {selectedTags.length}
                    </span>
                  )}
                  <ChevronDown className="h-3 w-3" strokeWidth={1.5} />
                </button>
                {tagPickerOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[9990]"
                      onClick={() => setTagPickerOpen(false)}
                    />
                    <div
                      className="absolute z-[9991] mt-1 w-[280px] rounded-md border bg-white p-3 shadow-lg"
                      style={{ borderColor: BORDER }}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-[11px] font-semibold uppercase text-muted-foreground">
                          Filter by tags
                        </span>
                        {selectedTags.length > 0 && (
                          <button
                            type="button"
                            className="text-[11px] text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setSelectedTags([]);
                              setPage(1);
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {noteTagOptions.map((tag) => {
                          const selected = selectedTags.includes(tag.value);
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => toggleTag(tag.value)}
                              style={{ opacity: selected ? 1 : 0.5 }}
                            >
                              <TagBadge value={tag.value} label={tag.label} size="sm" />
                            </button>
                          );
                        })}
                        {noteTagOptions.length === 0 && (
                          <span className="text-xs text-muted-foreground">No tags configured.</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <Select
                value={projectFilter}
                onChange={(e) => resetPage(setProjectFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
              <Select
                value={meetingFilter}
                onChange={(e) => resetPage(setMeetingFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Meetings</option>
                {meetings.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} — {m.title}
                  </option>
                ))}
              </Select>
            </div>

            {selectedTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground">Filtering:</span>
                {selectedTags.map((t) => (
                  <TagBadge
                    key={t}
                    value={t}
                    label={tagLabels[t] ?? t}
                    size="sm"
                    onRemove={() => toggleTag(t)}
                  />
                ))}
              </div>
            )}

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: BORDER }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                  Loading notes…
                </div>
              ) : allNotes.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No notes match the current filters.
                </div>
              ) : viewMode === "list" ? (
                renderTable(unpinned.length > 0 ? unpinned : allNotes)
              ) : (
                renderGrid(unpinned.length > 0 ? unpinned : allNotes)
              )}

              <div
                className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-[14px] py-[9px] text-xs text-muted-foreground"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center gap-3">
                  <span>
                    Showing {start}-{end} of {total} entries
                  </span>
                  <span className="flex items-center gap-1.5">
                    Show
                    <Select
                      value={String(pageSize)}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="h-7 w-[68px] text-xs"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </Select>
                    entries
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="px-2 text-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NewNoteDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(code) => showToast(`Note created: ${code}`)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete note?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Notes list.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
